import React, { useMemo } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import "@web/index.css";
import { Toaster } from "sonner";
import { AppRoutes } from "@web/app/routes";
import { preRenderSetup } from "@web/init";
import { ModalProvider } from "@web/features/modal";
import { DebugPanel } from "@web/features/debug";
import { PatchPilotProvider } from "@repo/patch-pilot-sdk/react";
import { useAuthStore } from "@web/stores";
import { Config } from "@web/config/env";
import { SentryFallback } from "@web/components/ui/sentry-fallback";

const PATCH_PILOT_OFFSET = { bottom: 90 } as const;

function PatchPilotConnector() {
  const user = useAuthStore((s) => s.user);
  const userInfo = useMemo(
    () => (user ? { email: user.email } : undefined),
    [user?.email],
  );
  return (
    <PatchPilotProvider
      apiKey={Config.PATCH_PILOT_API_KEY}
      apiUrl={Config.PATCH_PILOT_API_URL}
      user={userInfo}
      offset={PATCH_PILOT_OFFSET}
      theme="auto"
      locale="ko"
    />
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
});

const el = document.getElementById("root");
if (el) {
  preRenderSetup();
  const root = createRoot(el);
  root.render(
    <Sentry.ErrorBoundary fallback={SentryFallback}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppRoutes />
          <Toaster
            expand={false}
            visibleToasts={5}
            gap={8}
            toastOptions={{
              unstyled: true,
            }}
          />
          <ModalProvider />
          <DebugPanel />
          <PatchPilotConnector />
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>,
  );
} else {
  throw new Error("Could not find root element");
}
